import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useModal } from '../../../../../hooks/use-modal';
import useFetchShipFroms from '../../../../settings/ship-from-locations/use-fetch-ship-froms';
import ShipFromModal from '../../../../../ship-from/modals';
import { trackShipFromConfigStart } from '../../../../../ship-from/tracking-events';
import { EventSourceStrings } from '../../../../../tracking/tracking-constants';
import Global from '../../../../../store/global-selectors';
import { ACTION_TYPE_READY_TO_PICK } from '../../constants/action-types';
import { missingWarehouseBannerMessages } from './missing-warehouse-banner-messages';
import shipFromMessages from '../../../../settings/stores/selected-store/messages';
import style from './unsupported-rule-banner.module.less';

import type { AutomationRule } from '../../types/automation-rule-types';

type MissingWarehouseBannerProps = {
  selectedRule: AutomationRule | null;
};

const useHasMissingWarehouse = () => {
  const shipFroms = useSelector(Global.selectSellerShipFroms);
  const hasWarehouse = shipFroms && shipFroms.length > 0;

  return (selectedRule: AutomationRule | null): boolean => {
    // Check if rule has 'Ready to Pick' action
    const hasReadyToPickAction = selectedRule?.orderRuleActions?.some(
      (action) => action.actionTypeId === ACTION_TYPE_READY_TO_PICK && !action.isDeleted,
    );

    // Only show banner if action exists and no warehouse configured
    return Boolean(hasReadyToPickAction && !hasWarehouse);
  };
};

export const MissingWarehouseBanner = ({ selectedRule }: MissingWarehouseBannerProps) => {
  const hasMissingWarehouse = useHasMissingWarehouse();
  const { open, close, isOpen } = useModal();
  const { fetchShipFroms } = useFetchShipFroms();

  const onSuccess = useCallback(async () => {
    try {
      close();
      await fetchShipFroms();

      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...shipFromMessages.addShipFromSuccessMessageTitle} />,
        content: '',
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...shipFromMessages.addShipFromErrorMessageTitle} />,
        content: '',
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    }
  }, [fetchShipFroms, close]);

  const onClick = useCallback(() => {
    open();
    trackShipFromConfigStart(`automation-rules-${EventSourceStrings.MISSING_SHIP_FROM}`);
  }, [open]);

  // Only show if action exists and no warehouse configured
  if (!hasMissingWarehouse(selectedRule)) {
    return null;
  }

  return (
    <>
      <BannerMessageV2
        className={style.margin}
        content={
          <>
            <Text textType={TextType.Paragraph} value={<FormattedMessage {...missingWarehouseBannerMessages.description} />} />
            <Button onClick={onClick} theme={THEME_CONSTANTS.link}>
              <FormattedMessage {...missingWarehouseBannerMessages.addShipFromButton} />
            </Button>
          </>
        }
        header={<Text textType={TextType.Label} value={<FormattedMessage {...missingWarehouseBannerMessages.title} />} />}
        layout={BannerLayout.Full}
        status={BannerStatus.Warning}
      />
      {isOpen ? (
        <ShipFromModal onClose={close} onSuccess={onSuccess} source={`automation-rules-${EventSourceStrings.MISSING_SHIP_FROM}`} />
      ) : null}
    </>
  );
};
